import React from 'react';

import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { formatDateIST, formatIndianNumber } from '../../../voucher-form/components/common-functions/PaymentCommonFunctions';
import PaymentStatusCard from '../../../voucher-form/components/thank-you/PaymentStatusCard';
import { FORM_PHASE, getFormStatusStyles, getFrontEndStatus, PRIMARY_SOURCE } from '../../../../config/constant';
import greenTriangle from "../../../../assets/icons/green-triangle.svg"
import blueTriangle from "../../../../assets/icons/blue-triangle.svg"
import yellowTriangle from "../../../../assets/icons/yellow-triangle.svg"
import { useTranslation } from 'react-i18next';
import alertCircle from "../../../../assets/icons/alert-circle.svg";
import { checkIsMasked } from '../../../../utilis/common-funtions';
export const PreviewThankYou = ({ applicantData }) => {
  const statusStyles = getFormStatusStyles(applicantData?.voucherFormStatus);
  const { t } = useTranslation();

  const commonBoxStyles = {
    px: '12px',
    py: '6px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: '0.5rem',
  };

  // Left aligned
  const leftBoxStyles = {
    ...commonBoxStyles,
    justifyContent: 'flex-start',
  };

  // Right aligned
  const rightBoxStyles = {
    ...commonBoxStyles,
    justifyContent: 'space-between',
  };

  function extractPaymentDetails(data) {
    // Fallback to 0 if not provided
    const amountPayable = Number(data?.amountPayable) || 0;

    // Prefer `totalAmountPaid` if exists, else sum from transactions
    let amountPaid = Number(data?.totalAmountPaid) || 0;

    if (!amountPaid && Array.isArray(data?.transactions)) {
      amountPaid = data.transactions.reduce((sum, txn) => {
        const paid = Number(txn?.paidAmount) || 0;
        return sum + paid;
      }, 0);
    }

    // Ensure non-negative due
    let amountDue = amountPayable - amountPaid;
    if (amountDue < 0) amountDue = 0;

    return {
      amountPayable,
      amountPaid,
      amountDue,
    };
  }
  const isMasked = checkIsMasked(applicantData?.queueId);
  const PaymentObject = extractPaymentDetails(applicantData?.paymentDetails);
  return (
    <div className="applicantDetailsCard">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: 'wrap',
          gap: { xs: 2, sm: 1 },
        }}
      >
        {/* Left Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            flex: 1,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={leftBoxStyles}>
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {t("voucherPreview.thankYou.customerName")}:
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
              {`${applicantData?.applicant1?.personalDetails?.firstName} ${applicantData?.applicant1?.personalDetails?.lastName}`}
            </Typography>
          </Box>

          <Box sx={leftBoxStyles}>
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {t("voucherPreview.thankYou.mobNo")}:
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
              {`${applicantData?.applicant1?.personalDetails?.countryCode} ${applicantData?.applicant1?.personalDetails?.contactNumber}`}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-block',
              backgroundColor: 'rgba(0, 184, 217, 0.08)',
              px: '12px',
              py: '6px',
              borderRadius: '8px',
              width: 'fit-content',
            }}
          >
            <Typography
              sx={{
                color: 'rgba(0, 108, 156, 1)',
                fontSize: '14px',
                fontWeight: 700,
              }}
            >
              {applicantData.formPhase === FORM_PHASE.VOUCHER ? "Voucher" : "EOI"} ID: {applicantData?.uniqueReferenceId ?? '-'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: statusStyles.backgroundColor,
              px: "12px",
              py: "6px",
              borderRadius: "8px",
              width: "fit-content",
            }}
          >
            <Typography
              sx={{
                color: statusStyles.color,
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              {t("voucherPreview.thankYou.status")}: {getFrontEndStatus(applicantData?.voucherFormStatus)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>

            <Box
              sx={{
                backgroundColor: 'rgba(34, 197, 94, 0.08)',
                px: '12px',
                py: '6px',
                borderRadius: '8px',
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              <Typography
                sx={{
                  color: '#118D57',
                  fontSize: '14px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t("voucherPreview.thankYou.queueId")}: {applicantData?.queueId ?? '-'}
              </Typography>
            </Box>
            {isMasked && <Tooltip
              arrow
              enterTouchDelay={0}
              placement="right"
              title={t("voucherPreview.thankYou.maskedToolTip")}
              sx={{ cursor: "pointer" }}
            >
              <img src={alertCircle} alt="" style={{ cursor: "pointer" }} />
            </Tooltip>}
          </Box>



          <Box
            sx={{
              backgroundColor: 'rgba(142, 51, 255, 0.08)',
              px: '12px',
              py: '6px',
              borderRadius: '8px',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            <Typography
              sx={{
                color: 'rgba(81, 25, 183, 1)',
                fontSize: '14px',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t("voucherPreview.thankYou.preferredTypology")}: {applicantData?.eoiDetails?.typology ?? '-'}
            </Typography>
          </Box>
        </Box>

        {/* Right Section */}
        <Box
          sx={{
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={rightBoxStyles}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, width: '194px', }}> {t("voucherPreview.thankYou.initiatedDate")}:</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, textAlign: 'right', }}>
              {applicantData?.createdAt ? formatDateIST(applicantData?.createdAt) : ''}
            </Typography>
          </Box>

          {((applicantData?.formPhase === FORM_PHASE.VOUCHER) ||
            (applicantData?.formPhase === FORM_PHASE.EOI && applicantData?.voucherIssuedAt)) &&
            <Box sx={rightBoxStyles}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                {t("voucherPreview.thankYou.vouhcerQueueIdIssued")}:
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, textAlign: 'right' }}>
                {formatDateIST(applicantData?.voucherIssuedAt)}
              </Typography>
            </Box>}

          {applicantData?.formPhase === FORM_PHASE.EOI && 
          <Box sx={rightBoxStyles}>
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {t("voucherPreview.thankYou.eoiQueueIdIssued")}:
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, textAlign: 'right' }}>
              {formatDateIST(applicantData?.eoiIssuedAt)}
            </Typography>
          </Box>}

          <Box sx={rightBoxStyles}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, width: '194px', }}>
              {t("voucherPreview.thankYou.primarySource")}:
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, textAlign: 'right', }}>
              {applicantData?.primarySource}
            </Typography>
          </Box>

          {applicantData?.primarySource === PRIMARY_SOURCE.ChannelPartner && (
            <Box sx={rightBoxStyles}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, width: '194px', }}>
                {t("voucherPreview.thankYou.cpName")}:
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, textAlign: 'right', }}>
                {applicantData?.sourceDetails?.channelPartner}
              </Typography>
            </Box>
          )}

          {applicantData?.sourceDetails?.referredBy && (
            <Box sx={rightBoxStyles}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, width: '194px', }}>
                {t("voucherPreview.referrerDetails.referrerName")}:
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, textAlign: 'right', }}>
                {applicantData?.sourceDetails?.referredBy}
              </Typography>
            </Box>
          )}

          {applicantData?.primarySource === PRIMARY_SOURCE.PurvaChampion && (
            <Box sx={rightBoxStyles}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, width: '194px', }}>
                {t("voucherPreview.referrerDetails.empName")}:
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, textAlign: 'right', }}>
                {applicantData?.sourceDetails?.employeeName}
              </Typography>
            </Box>
          )}

        </Box>

        <Grid container spacing={2} sx={{
          mt: { xs: 0, md: 2 }
        }}>
          <Grid item xs={12} sm={6} md={4}>
            <PaymentStatusCard
              img={blueTriangle}
              amount={formatIndianNumber(PaymentObject?.amountPayable)}
              title={applicantData?.formPhase === FORM_PHASE.VOUCHER ? t("voucherPreview.thankYou.voucherAmt") : t("voucherPreview.thankYou.eoiAmt")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <PaymentStatusCard
              img={greenTriangle}
              amount={formatIndianNumber(PaymentObject?.amountPaid)}
              title={t("voucherPreview.thankYou.amountPaid")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <PaymentStatusCard
              img={yellowTriangle}
              amount={formatIndianNumber(PaymentObject?.amountDue || 0)}
              title={t("voucherPreview.thankYou.balanceDue")}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};